import { useEffect } from 'react';

import * as Platform from '@actual-app/core/shared/platform';

import { useNavigate } from '#hooks/useNavigate';
import { useIsNocturneTheme } from '#style/theme';

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
    target.closest('[contenteditable="true"], [role="textbox"]') !== null
  );
}

function isAvailable(element: HTMLElement) {
  if (
    element.hidden ||
    element.closest('[hidden], [aria-hidden="true"]') ||
    element.hasAttribute('data-disabled') ||
    element.getAttribute('aria-disabled') === 'true' ||
    element.matches(':disabled')
  ) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function hasOpenOverlay() {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      '[role="dialog"], [role="listbox"], [role="menu"]',
    ),
  ).some(isAvailable);
}

function focusNocturneSearch() {
  const search = Array.from(
    document.querySelectorAll<HTMLInputElement>('input[data-nocturne-search]'),
  ).find(isAvailable);

  search?.focus();
  return search != null;
}

function activateNocturneNewAction() {
  const action = Array.from(
    document.querySelectorAll<HTMLElement>('[data-nocturne-new]'),
  ).find(isAvailable);

  action?.click();
  return action != null;
}

function openNocturneCommandBar() {
  const event = new KeyboardEvent('keydown', {
    key: 'k',
    ctrlKey: true,
    bubbles: true,
    cancelable: true,
  });
  document.dispatchEvent(event);
  return event.defaultPrevented;
}

function claimNocturneShortcut(event: KeyboardEvent) {
  event.preventDefault();
  event.stopImmediatePropagation();
}

export function GlobalKeys() {
  const navigate = useNavigate();
  const isNocturne = useIsNocturneTheme();

  useEffect(() => {
    const handleKeys = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }

      const isBareKey =
        !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.repeat;

      if (
        isNocturne &&
        isBareKey &&
        !isEditableTarget(e.target) &&
        !hasOpenOverlay()
      ) {
        const routes: Record<string, string> = {
          '1': '/budget',
          '2': '/reports',
          '3': '/schedules',
          '4': '/rules',
        };
        const route = routes[e.key];

        if (route) {
          claimNocturneShortcut(e);
          void navigate(route);
          return;
        }

        if (
          e.key === '/' &&
          (focusNocturneSearch() || openNocturneCommandBar())
        ) {
          claimNocturneShortcut(e);
          return;
        }

        if (e.key.toLowerCase() === 'n' && activateNocturneNewAction()) {
          claimNocturneShortcut(e);
          return;
        }
      }

      if (Platform.isBrowser) {
        return;
      }

      if (e.metaKey) {
        switch (e.key) {
          case '1':
            void navigate('/budget');
            break;
          case '2':
            void navigate('/reports');
            break;
          case '3':
            void navigate('/accounts');
            break;
          case ',':
            if (Platform.OS === 'mac') {
              void navigate('/settings');
            }
            break;
          default:
        }
      }
    };

    document.addEventListener('keydown', handleKeys);

    return () => document.removeEventListener('keydown', handleKeys);
  }, [isNocturne, navigate]);

  return null;
}
